import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Copy, ExternalLink, Megaphone } from 'lucide-react'
import Card from '@/components/layout/Card'
import OffMarketWizard from '@/features/campaigns/OffMarketWizard'
import { useListingForms } from '@/features/forms/useForms'
import { copyText } from '@/features/forms/formBuilder'
import { formatShortDate } from '@/lib/dealFormat'
import { cn } from '@/lib/cn'
import type { Deal } from '@/api/types'

/**
 * The listing's off-market campaign on its deal: the enquiry form(s) with how many people enquired and
 * when, the link to share, and the enquirers list — plus "Launch off-market campaign". Admin-only
 * (forms are), so an agent's deal doesn't show it.
 */
export default function ListingCampaignCard({ deal }: { deal: Deal }) {
  const { data: forms, isLoading } = useListingForms(deal.id)
  const [launching, setLaunching] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  if (isLoading) return null
  const has = (forms ?? []).length > 0

  return (
    <Card
      title="Off-market campaign"
      action={
        has && (
          <button type="button" onClick={() => setLaunching(true)} className="text-xs font-medium text-brand-primary hover:text-brand-dark">
            + Another
          </button>
        )
      }
    >
      {!has ? (
        <div className="text-sm text-gray-600">
          <p className="mb-3">Offer this property to your buyers before it goes to market: an enquiry form, a list of everyone who enquires, and the announcement email — set up in one go.</p>
          <button
            type="button"
            onClick={() => setLaunching(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-brand-primary/40 text-sm font-medium text-brand-primary hover:bg-brand-primary/5"
          >
            <Megaphone size={15} /> Launch off-market campaign
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {(forms ?? []).map((f) => {
            const link = `${window.location.origin}/f/${f.id}`
            return (
              <li key={f.id} className="text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Link to={`/forms/${f.id}`} className="font-medium text-brand-dark hover:text-brand-primary truncate">{f.name}</Link>
                  <span className={cn('shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded-full', f.enabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
                    {f.enabled ? 'Live' : 'Paused'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  <Link to={`/forms/${f.id}?tab=submissions`} className="text-brand-primary hover:underline">
                    {f.submissionCount ?? 0} {f.submissionCount === 1 ? 'enquiry' : 'enquiries'}
                  </Link>
                  {f.lastSubmissionAt && <span> · last {formatShortDate(f.lastSubmissionAt)}</span>}
                </div>
                <div className="flex flex-wrap gap-3 mt-1 text-xs">
                  <button
                    type="button"
                    onClick={async () => {
                      if (await copyText(link)) {
                        setCopied(f.id)
                        setTimeout(() => setCopied(null), 1500)
                      }
                    }}
                    className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                  >
                    <Copy size={11} /> {copied === f.id ? 'Copied' : 'Copy form link'}
                  </button>
                  <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-primary hover:underline">
                    View form <ExternalLink size={11} />
                  </a>
                  {f.addToListId && (
                    <Link to={`/lists/${f.addToListId}`} className="inline-flex items-center gap-1 text-brand-primary hover:underline">
                      Enquirers list
                    </Link>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {launching && <OffMarketWizard deal={deal} onClose={() => setLaunching(false)} />}
    </Card>
  )
}
